import { PureComponent, ReactNode } from 'react';
import Fuse from 'fuse.js';

import PadV2AutoSuggest, {
    AllOptions,
    AS_OPTIONS_FREQUENCTLY,
    AS_OPTIONS_SEARCH,
    CustomDataForAS,
} from './PadV2AutoSuggest';
import { GLabVitalEntity } from '../../../../../store/db';
import { useDispatch, useSelector } from 'react-redux';
import { getLabVitals } from '../../../../../store/selectors/prescriptionPad';
import {
    applyPrescriptionTemplate,
    upsertLabVital,
} from '../../../../../store/actions/prescriptionPad';
import { LabVitalEntity } from '../../../../../store/types/prescription';
import fetchMatchingLabVitals, {
    LabVital,
    LabVitalsResponse,
} from '../../../../../api/fetchMatchingLabVitals';
import { LabVital as LabVitalIcon, templateIcon } from '../../../../common/Icons';
import { LabVitalTableComponent } from '../LabVitalTable';
import PadGenieAutoSuggestFuseManager, {
    LAB_VITALS_AND_TEMPLATES,
} from '../../../PadGenieAutoSuggestFuseManager';

const LAB_VITAL_AS_EL_ID = 'LAB_VITAL_AS_EL_ID';

type Props = {
    onSelect: (option: LabVital) => void;
    elements: ReadonlyArray<LabVitalEntity>;
};

export enum ENTITY_TYPE {
    LAB_RESULTS = 'lab_results',
    PANELS = 'panels',
}

const customDataForAS: CustomDataForAS = {
    test_results: {
        optionIconText: LabVitalIcon('fill-darwin-accent-bright-yellow-800'),
        optionIconBgClass: 'circle-lx-reading',
    },
    panels: {
        optionIconText: <div className="font-700 text-darwin-accent-bright-yellow-800">P</div>,
        optionIconBgClass: 'circle-lx-reading',
    },
};

class LabVitalAutoSuggest extends PureComponent<Props> {
    getOptions = (
        setOptions: (options: AllOptions<LabVital>, inputValue?: string) => void,
        input?: string,
    ): void => {
        const options: AllOptions<LabVital> = [];
        const fuse = PadGenieAutoSuggestFuseManager.getFuseForType(
            LAB_VITALS_AND_TEMPLATES,
        ) as Fuse<GLabVitalEntity>;
        const gElements = PadGenieAutoSuggestFuseManager.getOriginalListForType(
            LAB_VITALS_AND_TEMPLATES,
        ) as GLabVitalEntity[];

        if (!fuse) return;

        if (!input) {
            const genieOptions = gElements?.filter(Boolean) || [];
            const normalGenieOptions = genieOptions.filter((i) => !i.is_panel);
            const panelGenieOptions = genieOptions.filter((i) => i.is_panel);

            normalGenieOptions.splice(7);
            panelGenieOptions.splice(7);

            if (normalGenieOptions.length > 0) {
                options.push({
                    label: AS_OPTIONS_FREQUENCTLY,
                    options: normalGenieOptions,
                    autosuggestHeading: 'test results',
                });
            }
            if (panelGenieOptions.length > 0) {
                options.push({
                    label: AS_OPTIONS_FREQUENCTLY,
                    options: panelGenieOptions,
                    autosuggestHeading: 'panels',
                });
            }

            setOptions(options, input);
        } else {
            // search in genie
            const filteredGItems = fuse
                .search(input)
                .map((option) => option.item)
                .filter(Boolean)
                .map((t) => ({ ...t, track: { source: 'GENIE' } }));

            const normalGenieOptions = filteredGItems.filter((i) => !i.is_panel);
            const panelGenieOptions = filteredGItems.filter((i) => i.is_panel);

            normalGenieOptions.splice(7);
            panelGenieOptions.splice(7);

            if (normalGenieOptions.length > 0) {
                options.push({
                    label: AS_OPTIONS_SEARCH as typeof AS_OPTIONS_SEARCH,
                    options: normalGenieOptions,
                    autosuggestHeading: 'test results',
                    is_panel: false,
                });
            }
            if (panelGenieOptions.length > 0) {
                options.push({
                    label: AS_OPTIONS_SEARCH as typeof AS_OPTIONS_SEARCH,
                    options: panelGenieOptions,
                    autosuggestHeading: 'panels',
                    is_panel: true,
                });
            }

            setOptions(options, input);

            fetchMatchingLabVitals(input)
                .then(async (response) => {
                    this.handleApiOptions(options, input, setOptions, response);
                })
                .catch(() => {
                    this.handleApiOptions(options, input, setOptions);
                });
        }
    };

    handleApiOptions = (
        currentOptions: AllOptions<LabVital>,
        input: string,
        setOptions: (options: AllOptions<LabVital>, inputValue?: string) => void,
        response?: LabVitalsResponse,
    ): void => {
        const options: AllOptions<LabVital> = [];

        let allNormalOptions = currentOptions.find((i) => !i.is_panel)?.options || [];
        let allPanelOptions = currentOptions.find((i) => i.is_panel)?.options || [];

        response
            ?.filter(Boolean)
            .filter((i) => !i.is_panel)
            .forEach((i) => {
                if (!allNormalOptions.find((s) => s.id === i.id)) {
                    allNormalOptions.push(i);
                }
            });
        response
            ?.filter(Boolean)
            .filter((i) => i.is_panel)
            .forEach((i) => {
                if (!allPanelOptions.find((s) => s.id === i.id)) {
                    allPanelOptions.push(i);
                }
            });

        allNormalOptions.splice(7);
        allPanelOptions.splice(7);

        allNormalOptions = allNormalOptions.map((i) => ({
            ...i,
            type: ENTITY_TYPE.LAB_RESULTS,
        }));
        allPanelOptions = allPanelOptions.map((i) => ({
            ...i,
            type: ENTITY_TYPE.PANELS,
        }));

        if (allNormalOptions.length > 0) {
            options.push({
                label: AS_OPTIONS_SEARCH,
                options: allNormalOptions,
                autosuggestHeading: 'test results',
            });
        }
        if (allPanelOptions.length > 0) {
            options.push({
                label: AS_OPTIONS_SEARCH,
                options: allPanelOptions,
                autosuggestHeading: 'panels',
            });
        }

        // const isMatchFound = allOptions.find(
        //     (i) => i.name.toLowerCase() === inputValue.toLowerCase(),
        // );

        // if (!isMatchFound && inputValue) {
        //     allOptions.push({
        //         id: `${LOCAL_ID_PREFIX}${
        //             kind === SYMPTOMS ? 'sym' : 'dia'
        //         }-${new Date().getTime()}-${randomString(4)}`,
        //         name: inputValue,

        //         _ui: { kind, source: 'CUSTOM' },
        //     });
        // }

        setOptions(options, input);
    };

    OptionIcon = LabVitalIcon('fill-darwin-accent-bright-yellow-800');
    TemplateIcon = templateIcon('fill-darwin-accent-bright-yellow-900');

    render(): ReactNode {
        const { elements, onSelect } = this.props;
        return (
            <PadV2AutoSuggest
                elId={LAB_VITAL_AS_EL_ID}
                onSelect={onSelect}
                optionIconText={this.OptionIcon}
                templateIconText={this.TemplateIcon}
                optionIconBgClass="circle-lx-reading"
                menuListClassName="lx-reading-dropdown"
                optionWrapperClass="lx-reading-dropdown-item"
                getOptions={this.getOptions}
                placeholder="Start typing a parameter..."
                elements={elements}
                hideKeyboardShortCutsInfo
                customDataForAS={customDataForAS}
            />
        );
    }
}

const LabVitalASComp = LabVitalAutoSuggest;

export default LabVitalASComp;

export function toPxLabVitalFromApiObj(option: LabVital | LabVitalEntity): LabVitalEntity {
    return {
        id: option.id,
        name: option.name,
        unit_dislay_name: option.unit_dislay_name,
        value: undefined,
        interpretation: undefined,
        date: new Date().toISOString(),
        remark: undefined,
        unit:
            'unit' in option && option.unit
                ? option.unit
                : option.all_units && option.all_units.length > 0
                ? option.all_units[0]
                : undefined,
        all_units: option.all_units,
        is_panel: option.is_panel,
    };
}

export const LabVitalOnPadAutoSuggest: React.FC = () => {
    const dispatch = useDispatch();
    const elements = useSelector(getLabVitals);

    const onSelect = (option: (LabVital | GLabVitalEntity) & { isTemplate?: boolean }) => {
        if (option.isTemplate) {
            dispatch(applyPrescriptionTemplate('labVitals', option.id));
        } else {
            setTimeout(() => {
                const rowRef = LabVitalTableComponent.REF_MAP[elements.length];
                rowRef &&
                    rowRef[
                        option?.all_units &&
                        option?.all_units?.length > 0 &&
                        (!('unit' in option) || !option.unit)
                            ? 1
                            : 2
                    ]?.focus();
            }, 400);
            dispatch(upsertLabVital(toPxLabVitalFromApiObj(option), elements.length));
        }
    };

    return <LabVitalASComp onSelect={onSelect} elements={elements} />;
};
